import { useId, useMemo, useRef, useState, type ReactNode } from "react";

import { filterAndRank } from "./search";

/**
 * Champ de saisie à autocomplétion dynamique — remplace les listes déroulantes
 * `<select>` des outils. Générique : il ne connaît ni véhicules ni arrêts, il
 * reçoit des `options`, les termes sur lesquels chercher et leur rendu.
 *
 * Comportement :
 *  - filtre en temps réel à chaque frappe (casse et accents ignorés, voir
 *    `search.ts`), meilleures correspondances d'abord (exacte > préfixe > contenu) ;
 *  - choix à la souris, ou aux flèches ↑/↓ puis Entrée ; Échap ferme ;
 *  - motif ARIA « combobox à liste » (rôle, `aria-activedescendant`), le focus
 *    ne quitte jamais le champ.
 *
 * Deux usages, sans état dupliqué :
 *  - **action** (`selected` non fourni) : le choix déclenche `onSelect` et le
 *    champ se vide ;
 *  - **valeur** (`selected` fourni) : le champ affiche `getSelectedText(selected)` ;
 *    retaper dans le champ appelle `onSelect(null)` (on invalide le choix).
 */
export interface AutocompleteProps<T> {
  /** Libellé accessible (le champ n'affiche pas de `<label>` lui-même). */
  label: string;
  options: readonly T[];
  getKey: (option: T) => string;
  /** Textes sur lesquels porte la recherche (un option peut en avoir plusieurs). */
  getSearchTerms: (option: T) => ReadonlyArray<string | null | undefined>;
  renderOption: (option: T) => ReactNode;
  onSelect: (option: T | null) => void;
  selected?: T | null;
  /** Texte affiché dans le champ pour l'option choisie (obligatoire avec `selected`). */
  getSelectedText?: (option: T) => string;
  /** Notifié à chaque frappe de l'utilisateur (saisie libre parallèle au choix). */
  onInputChange?: (text: string) => void;
  placeholder?: string;
  /** Nombre maximum de suggestions affichées (défaut 8). */
  maxResults?: number;
  emptyText?: string;
  inputMode?: "text" | "numeric" | "search";
  disabled?: boolean;
}

export function Autocomplete<T>({
  label,
  options,
  getKey,
  getSearchTerms,
  renderOption,
  onSelect,
  selected = null,
  getSelectedText,
  onInputChange,
  placeholder,
  maxResults = 8,
  emptyText = "Aucun résultat.",
  inputMode = "text",
  disabled = false,
}: AutocompleteProps<T>) {
  const uid = useId();
  const listId = `${uid}-list`;
  const optionId = (key: string) => `${uid}-opt-${key}`;

  // `draft` = ce que l'utilisateur est en train de taper ; `null` = on affiche
  // l'option choisie (ou rien). Évite tout effet de synchronisation.
  const [draft, setDraft] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const text = draft ?? (selected && getSelectedText ? getSelectedText(selected) : "");
  // Suggestions seulement pendant la frappe : un champ qui affiche déjà le
  // choix validé ne rouvre pas de liste par-dessus.
  const query = draft ?? "";

  const matches = useMemo(
    () => filterAndRank(options, query, getSearchTerms),
    [options, query, getSearchTerms],
  );
  const shown = matches.slice(0, maxResults);
  const hidden = matches.length - shown.length;
  const expanded = open && query.trim() !== "";
  const safeActive = Math.min(activeIndex, Math.max(shown.length - 1, 0));
  const activeOption = expanded ? shown[safeActive] : undefined;

  const choose = (option: T) => {
    setDraft(null);
    setOpen(false);
    setActiveIndex(0);
    onSelect(option);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!expanded) setOpen(true);
        else setActiveIndex(Math.min(safeActive + 1, shown.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (expanded) setActiveIndex(Math.max(safeActive - 1, 0));
        break;
      case "Enter":
        // Ne valide que si une suggestion est affichée ; sinon laisse le
        // formulaire parent (s'il y en a un) suivre son cours.
        if (activeOption !== undefined) {
          e.preventDefault();
          choose(activeOption);
        }
        break;
      case "Escape":
        if (expanded) {
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
        }
        break;
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        className="input is-small"
        aria-label={label}
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={listId}
        aria-activedescendant={activeOption !== undefined ? optionId(getKey(activeOption)) : undefined}
        autoComplete="off"
        spellCheck={false}
        inputMode={inputMode}
        placeholder={placeholder}
        disabled={disabled}
        value={text}
        onChange={(e) => {
          const next = e.currentTarget.value;
          setDraft(next);
          setOpen(true);
          setActiveIndex(0);
          if (selected !== null) onSelect(null); // retaper invalide le choix précédent
          onInputChange?.(next);
        }}
        onFocus={(e) => {
          setOpen(true);
          e.currentTarget.select(); // champ déjà rempli : la frappe remplace le choix
        }}
        onBlur={() => {
          setOpen(false);
          // Champ quitté sans choix : on revient à l'option validée (ou à vide),
          // sauf saisie libre suivie par le parent (`onInputChange`).
          if (!onInputChange) setDraft(null);
        }}
        onKeyDown={onKeyDown}
      />

      {expanded ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "calc(100% + 2px)",
            zIndex: 20,
            margin: 0,
            padding: 2,
            listStyle: "none",
            maxHeight: 280,
            overflowY: "auto",
            background: "var(--sentinelle-surface)",
            border: "1px solid var(--sentinelle-rule)",
            borderRadius: 4,
            boxShadow: "0 4px 14px rgba(0,0,0,.22)",
          }}
        >
          {shown.length === 0 ? (
            <li role="presentation" className="has-text-grey is-size-7" style={{ padding: "6px 8px" }}>
              {emptyText}
            </li>
          ) : (
            shown.map((option, i) => {
              const key = getKey(option);
              const active = i === safeActive;
              return (
                <li
                  key={key}
                  id={optionId(key)}
                  role="option"
                  aria-selected={active}
                  // mousedown (pas click) : choisir AVANT que le blur du champ ne
                  // ferme la liste, sans jamais perdre le focus.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(option);
                  }}
                  onMouseEnter={() => setActiveIndex(i)}
                  style={{
                    padding: "5px 8px",
                    cursor: "pointer",
                    borderRadius: 3,
                    background: active ? "var(--sentinelle-scheme-main-ter, rgba(43,108,176,.14))" : undefined,
                  }}
                >
                  {renderOption(option)}
                </li>
              );
            })
          )}
          {hidden > 0 ? (
            <li role="presentation" className="has-text-grey" style={{ padding: "4px 8px", fontSize: "0.7rem" }}>
              + {hidden} autre{hidden > 1 ? "s" : ""} — affiner la saisie
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
